import { useCallback } from 'react';
import Vendor from 'data/Vendor';
import { BusinessUnit } from 'data/BusinessUnit';
import { STORAGE_KEYS } from 'constants/general';

interface UseVendorBusinessUnitPersistenceReturn {
  persistVendorSelection: (vendor: Vendor | null) => void;
  persistBusinessUnitSelection: (businessUnit: BusinessUnit | null) => void;
  restoreSelectionsFromStorage: (vendors: Vendor[], businessUnits: BusinessUnit[]) => {
    vendor: Vendor;
    businessUnit: BusinessUnit;
  } | null;
  clearStoredSelections: () => void;
}

/**
 * Custom hook for handling vendor and business unit persistence in localStorage
 * Provides utilities for saving, restoring, and validating selections
 */
export const useVendorBusinessUnitPersistence = (): UseVendorBusinessUnitPersistenceReturn => {
  
  const persistVendorSelection = useCallback((vendor: Vendor | null) => {
    if (vendor) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_VENDOR, JSON.stringify(vendor));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_VENDOR);
    }
  }, []);

  const persistBusinessUnitSelection = useCallback((businessUnit: BusinessUnit | null) => {
    if (businessUnit) {
      localStorage.setItem(STORAGE_KEYS.SELECTED_BUSINESS_UNIT, JSON.stringify(businessUnit));
    } else {
      localStorage.removeItem(STORAGE_KEYS.SELECTED_BUSINESS_UNIT);
    }
  }, []);

  const restoreSelectionsFromStorage = useCallback((vendors: Vendor[], businessUnits: BusinessUnit[]) => {
    try {
      const storedVendor = localStorage.getItem(STORAGE_KEYS.SELECTED_VENDOR);
      const storedBusinessUnit = localStorage.getItem(STORAGE_KEYS.SELECTED_BUSINESS_UNIT);
      
      if (storedVendor && storedBusinessUnit) {
        const parsedVendor = JSON.parse(storedVendor) as Vendor;
        const parsedBusinessUnit = JSON.parse(storedBusinessUnit) as BusinessUnit;
        
        // Validate that the stored selections still exist in current data.
        // A unit can be deactivated after it was stored, and its id still
        // resolves because deactivated units stay in the list, so the stored
        // selection is dropped unless the unit is still active.
        const validVendor = vendors.find(v => v.id === parsedVendor.id);
        const validBusinessUnit = businessUnits.find(
          bu => bu.id === parsedBusinessUnit.id && !bu.deletedOn
        );
        
        if (validVendor && validBusinessUnit) {
          return { vendor: validVendor, businessUnit: validBusinessUnit };
        }
      }
    } catch (error) {
      console.warn('Failed to restore selections from storage:', error);
    }
    return null;
  }, []);

  const clearStoredSelections = useCallback(() => {
    localStorage.removeItem(STORAGE_KEYS.SELECTED_VENDOR);
    localStorage.removeItem(STORAGE_KEYS.SELECTED_BUSINESS_UNIT);
  }, []);

  return {
    persistVendorSelection,
    persistBusinessUnitSelection,
    restoreSelectionsFromStorage,
    clearStoredSelections
  };
};